import { Button } from "@/components/ui/button";
import {
    Card,
} from "@/components/ui/card";
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from "react-hook-form";

import { CircleAlert } from 'lucide-react';
import { schema, type schemaType } from './schema';

import { useState } from 'react';

import { DEFAULT_CONFIGURATION_FOR_CRM_SYSTEM } from '@/lib/utils/crm/defaultConfiguration';
import { Configuration } from "@/types/crm/configuration/configuration";
import { useParams } from 'react-router-dom';

import { Cable, Info, Unplug } from 'lucide-react';

import { useEditMultipleProjectsWithDifferentData } from '@/lib/useQuery/project/useProjects';

import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useCreateCrm } from '@/features/crm/hooks/useMutation/useCreateCrm';
import { Json } from '@/lib/supabase/supabase_types';
import { useCreateCrmProject } from '@/features/crm_project/hooks/useMutation/useCreateCrmProject';
import { AirtableBase } from "@/types/crm/airtable/common";
import { SelectBaseModal } from './SelectBaseModal';


export type Project = {
    id: string;
    name: string;
}

type Props = {
    data: {
        1: {
            data: Project[];
        };
        2: {
            data: {
                name: string;
                system: string;
            };
        };
        3: {
            data: {
                apiKey: string;
                bases: AirtableBase[];
            };
        }
    };
    onBack: () => void;
    onClose: () => void;
}

const Step4: React.FC<Props> = ({
    data,
    onBack,
    onClose,
}) => {
    const { orgId } = useParams();
    const { mutateAsync: createCrm } = useCreateCrm();
    const { mutateAsync: createMultipleCrmProjects } = useCreateCrmProject();
    const { mutateAsync: associateCrmConfigurationsWithProjects } = useEditMultipleProjectsWithDifferentData();


    const [showSelectBaseModal, setShowSelectBaseModal] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

    const openBaseModalHandler = (projectId: string) => {
        setSelectedProjectId(projectId);
        setShowSelectBaseModal(true);
    }

    const onCloseBaseModalHandler = () => {
        setSelectedProjectId(null);
        setShowSelectBaseModal(false);
    }

    const {
        handleSubmit,
        setValue,
        watch,
        control,
    } = useForm<schemaType>({ 
        resolver: zodResolver(schema),
        defaultValues: {
            projects: data[1].data.map((project) => ({
                    projectId: project.id,
                    name: project.name,
                    base: {
                        id: '',
                        name: '',
                    }
            }))
        }
    });

    const { fields } = useFieldArray({
        control,
        name: 'projects',
    });
    const watchFields = watch('projects');

    const onSelectBaseHandler = (base: AirtableBase) => {
        if(!selectedProjectId || !base || !watchFields) return;

        const projectIndex = watchFields.findIndex((project) => {
            return project.projectId === selectedProjectId
        });

        if(projectIndex !== -1) {
            setValue(`projects.${projectIndex}.base`, {
                id: base.id,
                name: base.name,
            });
            setShowSelectBaseModal(false);
            SuccessPopup("Base has been linked with the project.");
        } else {
            ErrorPopup("Project not found");
        }
    }

    const onDisconnectBaseHandler = (projectId: string) => {
        if(!watchFields) return;
        const projectIndex = watchFields.findIndex((project) => {
            return project.projectId === projectId
        });
        if(projectIndex !== -1) {
            setValue(`projects.${projectIndex}.base`, {
                id: '',
                name: '',
            });
            SuccessPopup("Base has been unlinked from the project.");
        }
    }

    const createCrmHandler = async ({
        name,
        system,
        default_configuration,
        apiKey,
    }: {
        name: string;
        system: string;
        default_configuration: Configuration;
        apiKey: string;
    }) => {
        const reqBody = {
            name: name,
            crm_system: system,
            configuration: {
                parameter: {
                    project: [],
                    survey: [],
                    question: [],
                }
            },
            configuration_data: {
                parameter: {
                    question: {}
                }
            },
            default_configuration: default_configuration,
            selected_configuration: {
                parameter: [
                    {
                        id: "Api Key",
                        value: apiKey
                    }
                ]
            }
        }
        
        try {
            const createdCrmData = await createCrm({...reqBody, organisation_id: Number(orgId)});
            return createdCrmData?.data?.id;
        } catch (error: URIError | unknown) {
            if (error instanceof Error) {
                ErrorPopup(error.message);
            } else {
                ErrorPopup("An unknown error occurred");
            }
        }
        return null;
    }

    const createCrmProjectsHandler = async ({
        crmId,
        formData,
    } : {
        crmId: number | null;
        formData: schemaType;
    }) => {
        if(!crmId) {
            ErrorPopup("Failed to create CRM configuration.");
            return null;
        }

        if(!formData.projects || formData.projects.length === 0) {
            ErrorPopup("Please select a project.");
            return null;
        }

        const reqBody = formData.projects.map((project) => {
            return {
                name: project.base.name,
                crm_id: crmId,
                selected_configuration: {
                    parameter: [{
                        id: 'Base Id',
                        value: project.base.id
                    }]
                }
            }
        })

        try {
            const crmProjectResult = await createMultipleCrmProjects(reqBody);
            return crmProjectResult?.data;
        } catch (error: URIError | unknown) {
            if (error instanceof Error) {
                ErrorPopup(error.message);
            } else {
                ErrorPopup("An unknown error occurred");
            }
        }
        return null;
    }

    const associateProjectsWithCrmHandler = async ({
        crmId,
        formData,
        crmBasicData,
        crmProjectsData,
    }:{
        crmId: number | null;
        formData: schemaType;
        crmBasicData: {
            name: string;
            system: string;
        };
        crmProjectsData: {
            archived: boolean;
            created_at: string;
            crm_id: number | null;
            id: number;
            name: string;
            selected_configuration: Json | null;
        }[] | null;
    }) => {
        if(!crmId) {
            ErrorPopup("Failed to create CRM configuration.");
            return null;
        }

        if(!formData.projects || formData.projects.length === 0) {
            ErrorPopup("Please select a project.");
            return null;
        }

        if(!crmProjectsData) {
            ErrorPopup("Failed to create CRM projects.");
            return null;
        }

        const getProjectCRMidByName = (projectName: string, responseBody: {name: string, id: number}[]) => {
            return responseBody.find((project) => project.name === `${projectName}`)?.id;
        }

        // 3. associate projects with created crm configurations
        const reqBody = formData.projects.map((project) => {
            return {
                id: project.projectId,
                crm_id: crmId,
                crm_system: crmBasicData.system,
                crm_project_id: getProjectCRMidByName(project.base.name, crmProjectsData),
            }
        });

        try {
            await associateCrmConfigurationsWithProjects({
                data: reqBody,
            });
            SuccessPopup("CRM configured successfully");
            onClose();
        } catch (error: URIError | unknown) {
            if (error instanceof Error) {
                ErrorPopup(error.message);
            } else {
                ErrorPopup("An unknown error occurred");
            }
        }
    }

    const onSubmitHandler = async (formData: schemaType) => {
            if((!formData.projects) || (formData.projects.length === 0)) {
                ErrorPopup("Please select at least one project to link with the base.");
                return;
            }

            const crmBasicData = data[2].data;
            const airtableData = data[3].data;


            const crmId = await createCrmHandler({
                name: crmBasicData.name,
                system: crmBasicData.system,
                default_configuration: DEFAULT_CONFIGURATION_FOR_CRM_SYSTEM.airtable,
                apiKey: airtableData.apiKey,
            });

            const crmProjectsData = await createCrmProjectsHandler({
                crmId: crmId,
                formData: formData,
            })

            await associateProjectsWithCrmHandler({
                crmId: crmId,
                formData: formData,
                crmBasicData: crmBasicData,
                crmProjectsData: crmProjectsData,
            })
            
    }

    const onNextHandler = () => {
        handleSubmit((data) => {
            onSubmitHandler(data);
        }, (errors) => {
            console.log("[Step4] errors: ", errors);
        })();
    }

    return (
        <>
            <SelectBaseModal 
                show={showSelectBaseModal}
                onClose={onCloseBaseModalHandler}
                bases={data[3].data.bases}
                onSelectBase={onSelectBaseHandler}
            />

            <div className="flex items-center my-2 text-blue-600">
                <Info className="flex-shrink-0 me-1" size="16"/>
                <p className="text-xs">Now you need to create a configuration that will link your specific project to a specific base in Airtable. You will also need to manually connect your surveys to the corresponding tables.</p>
            </div>

            <div className="flex flex-col gap-3">
                { fields.map((project, index) => (
                    <Card className="flex items-center justify-between px-3 py-2" key={index}>
                        
                        <div className="flex flex-col items-start">
                            <h2 className="font-semibold">{project.name}</h2>
                            <p className={cn("flex gap-1 items-center text-sm my-2", !(watchFields?.[index]?.base?.name) ? "text-destructive" : "text-blue-600")}>
                                {!(watchFields?.[index]?.base?.name) && (
                                    <CircleAlert
                                        width={16}
                                        height={16}
                                    />
                                )}
                                
                                Connected to the base: <b>{watchFields?.[index]?.base?.name || '-'}</b>
                            </p>
                        </div>
                            
                        <TooltipProvider>
                            <div>
                                {!(watchFields?.[index]?.base?.name) && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                size={"action"}
                                                onClick={() => openBaseModalHandler(project.projectId)}
                                            ><Cable size="18"/></Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Connect</p>
                                        </TooltipContent>
                                    </Tooltip>
                                )}

                                {(watchFields?.[index]?.base?.name) && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                size={"action"}
                                                variant="destructive"
                                                onClick={() => onDisconnectBaseHandler(project.projectId)}
                                            ><Unplug size="18"/></Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Disconnect</p>
                                        </TooltipContent>
                                </Tooltip>
                                )}
                            </div>
                        </TooltipProvider>
                        
                    </Card>
                ))}
            </div>
            <div className="flex items-stretch justify-between w-full pt-3 m-0 mt-3 border-t sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                
                <div className="flex m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                    <Button variant="secondary" onClick={onBack}>Back</Button>
                    <Button onClick={onNextHandler}>Next</Button>
                </div>
            </div>
        </>
    )
}

export { Step4 };

